import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "./scroll";

const LINKS = [
  { label: "Mission", href: "#mission" },
  { label: "Adam's Way", href: "#adams-way" },
  { label: "Impact", href: "#impact" },
  { label: "News", href: "#news" },
  { label: "Contact", href: "#contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-bone/80 backdrop-blur-xl border-foreground/5"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          data-testid="nav-logo-link"
          className="flex items-center shrink-0"
        >
          <img
            src="/logo.png"
            alt="The Next Step Housing Corp"
            className="h-12 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/['\s]/g, "-")}`}
              className="link-line text-sm font-medium tracking-wide text-foreground/80 hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#get-involved"
            onClick={(e) => go(e, "#get-involved")}
            data-testid="nav-donate-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5"
          >
            Donate
          </a>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {open ? <MenuIconClose /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-bone/95 backdrop-blur-xl border-b border-foreground/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="font-serif text-2xl text-foreground"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#get-involved"
                onClick={(e) => go(e, "#get-involved")}
                className="mt-2 inline-flex w-fit items-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground"
              >
                Donate
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const MenuIconClose = () => <X size={22} />;

export default Nav;
